import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp,
  getDoc
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";

// Import shop variables
import { SHOP_NAME, SHOP_ADDRESS, SHOP_PHONE } from '../Variables';
// Import logo
import shopLogo from '../images/shop_logo.png';

Modal.setAppElement('#root');

const PurchaseOrders = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState([]);
  const [bills, setBills] = useState([]);

  const [newPurchaseOrder, setNewPurchaseOrder] = useState({
    poNumber: '',
    vendorId: '',
    vendorName: '',
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    description: '',
    status: 'Open',
    userId: user?.uid || ''
  });
  const [editingPurchaseOrder, setEditingPurchaseOrder] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [isUnmatchedBillsModalOpen, setIsUnmatchedBillsModalOpen] = useState(false);
  const [currentPurchaseOrder, setCurrentPurchaseOrder] = useState(null);
  const [unmatchedBillsSection, setUnmatchedBillsSection] = useState(null);
  const [isFetchingPurchaseOrders, setIsFetchingPurchaseOrders] = useState(true);
  const [hasFetchedPurchaseOrders, setHasFetchedPurchaseOrders] = useState(false);
  
  // Bulk import states
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');

  const purchaseOrdersCollectionRef = collection(db, "purchaseOrders");
  const vendorsCollectionRef = collection(db, "vendors");
  const billsCollectionRef = collection(db, "bills");

  useEffect(() => {
    if (user && userData) {
      fetchVendors();
      fetchBills();
      fetchPurchaseOrders();
    }
  }, [user, userData]);

  useEffect(() => {
    const filtered = purchaseOrders.filter((po) =>
      po.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.vendorName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPurchaseOrders(filtered);
  }, [searchTerm, purchaseOrders]);

  const fetchVendors = async () => {
    if (!user?.uid) return;
    try {
      const q = query(vendorsCollectionRef, where("active", "==", true));
      const data = await getDocs(q);
      const vendorList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setVendors(vendorList);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setError("Failed to fetch vendors");
      setShowModal(true);
    }
  };

  const fetchBills = async () => {
    if (!user?.uid) return;
    try {
      const q = query(billsCollectionRef);
      const data = await getDocs(q);
      const billPromises = data.docs.map(async (doc) => {
        const billData = { ...doc.data(), id: doc.id };
        // Try to get vendor name from bill or lookup
        const vendorName = billData.vendorName || 'Unknown';
        return { ...billData, vendorName };
      });
      const resolvedBills = await Promise.all(billPromises);
      setBills(resolvedBills);
    } catch (error) {
      console.error("Error fetching bills:", error);
      setError("Failed to fetch bills");
      setShowModal(true);
    }
  };

  const fetchPurchaseOrders = async () => {
    if (!user?.uid) return;
    
    setIsFetchingPurchaseOrders(true);
    try {
      const q = query(purchaseOrdersCollectionRef);
      const data = await getDocs(q);
      const poList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setPurchaseOrders(poList);
      setHasFetchedPurchaseOrders(true);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      setError("Failed to fetch purchase orders");
      setShowModal(true);
    } finally {
      setIsFetchingPurchaseOrders(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'vendorId') {
      const selectedVendor = vendors.find(v => v.id === value);
      setNewPurchaseOrder({ 
        ...newPurchaseOrder, 
        [name]: value,
        vendorName: selectedVendor?.name || ''
      });
    } else {
      setNewPurchaseOrder({ ...newPurchaseOrder, [name]: value });
    }
  };

  const handleAddPurchaseOrder = async () => {
    if (!newPurchaseOrder.poNumber?.trim() || !newPurchaseOrder.vendorId || newPurchaseOrder.amount <= 0) {
      setError("PO number, vendor, and positive amount are required");
      setShowModal(true);
      return;
    }
    
    setUpdate(true);
    try {
      const selectedVendor = vendors.find(v => v.id === newPurchaseOrder.vendorId);
      if (!selectedVendor) {
        setError("Selected vendor not found");
        setShowModal(true);
        return;
      }
      
      await addDoc(purchaseOrdersCollectionRef, {
        poNumber: newPurchaseOrder.poNumber.trim(),
        vendorId: newPurchaseOrder.vendorId,
        vendorName: selectedVendor.name,
        date: newPurchaseOrder.date,
        amount: parseFloat(newPurchaseOrder.amount),
        description: newPurchaseOrder.description || '',
        status: newPurchaseOrder.status,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Purchase order added successfully");
      await fetchPurchaseOrders();
      await fetchBills();
      resetNewPurchaseOrder();
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Add PO error:", error);
      setError("Failed to add purchase order");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdatePurchaseOrder = async () => {
    if (!newPurchaseOrder.poNumber?.trim() || !newPurchaseOrder.vendorId || newPurchaseOrder.amount <= 0) {
      setError("PO number, vendor, and positive amount are required");
      setShowModal(true);
      return;
    }
    
    // Prevent setting status to 'Matched' manually
    if (newPurchaseOrder.status === 'Matched') {
      setError("Status cannot be set to 'Matched' manually. Use the match button to link bills.");
      setShowModal(true);
      return;
    }
    
    setUpdate(true);
    try {
      const selectedVendor = vendors.find(v => v.id === newPurchaseOrder.vendorId);
      if (!selectedVendor) {
        setError("Selected vendor not found");
        setShowModal(true);
        return;
      }
      
      const poDoc = doc(db, "purchaseOrders", editingPurchaseOrder.id);
      await updateDoc(poDoc, {
        poNumber: newPurchaseOrder.poNumber.trim(),
        vendorId: newPurchaseOrder.vendorId,
        vendorName: selectedVendor.name,
        date: newPurchaseOrder.date,
        amount: parseFloat(newPurchaseOrder.amount),
        description: newPurchaseOrder.description || '',
        status: newPurchaseOrder.status // Will only be 'Open' or 'Cancelled'
      });
      setSuccessMessage("Purchase order updated successfully");
      await fetchPurchaseOrders();
      await fetchBills();
      resetNewPurchaseOrder();
      setEditingPurchaseOrder(null);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Update PO error:", error);
      setError("Failed to update purchase order");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleMatchPurchaseOrder = async (billId) => {
    setUpdate(true);
    try {
      const billDoc = doc(db, "bills", billId);
      const billSnap = await getDoc(billDoc);
      if (!billSnap.exists()) {
        throw new Error("Bill not found");
      }
      const bill = { id: billSnap.id, ...billSnap.data() };
      
      // Verify vendor match
      if (bill.vendorId !== currentPurchaseOrder.vendorId) {
        setError("Vendor mismatch between bill and purchase order");
        setShowModal(true);
        return;
      }
      
      // Check amount match
      const amountDiff = Math.abs(bill.amount - currentPurchaseOrder.amount);
      if (amountDiff > 0.01) {
        setError(`Amount mismatch: ₦${amountDiff.toFixed(2)} difference`);
        setShowModal(true);
        return;
      }
      
      // Check if bill is already matched
      if (bill.purchaseOrderId) {
        setError("Bill is already matched to another purchase order");
        setShowModal(true);
        return;
      }
      
      // Update PO - Set status to 'Open' but add matched fields (display will handle 'Matched with...')
      const poDoc = doc(db, "purchaseOrders", currentPurchaseOrder.id);
      await updateDoc(poDoc, { 
        status: 'Open',
        matchedBillId: billId,
        matchedBillNumber: bill.billNumber // Store bill number for display
      });
      
      // Update bill
      await updateDoc(billDoc, { 
        purchaseOrderId: currentPurchaseOrder.id 
      });
      
      setSuccessMessage(`PO ${currentPurchaseOrder.poNumber} matched with bill ${bill.billNumber}`);
      await fetchPurchaseOrders();
      await fetchBills();
      setIsMatchModalOpen(false);
    } catch (error) {
      console.error("Match error:", error);
      setError("Failed to match purchase order: " + error.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const openMatchModal = (po) => {
    setCurrentPurchaseOrder(po);
    setIsMatchModalOpen(true);
  };

  const openUnmatchedBills = (vendorName) => {
    const vendorBills = bills.filter(bill => 
      bill.vendorName === vendorName && 
      !bill.purchaseOrderId
    );
    setUnmatchedBillsSection({
      vendorName,
      bills: vendorBills
    });
    setIsUnmatchedBillsModalOpen(true);
  };

  const resetNewPurchaseOrder = () => {
    setNewPurchaseOrder({
      poNumber: '',
      vendorId: '',
      vendorName: '',
      date: new Date().toISOString().split('T')[0],
      amount: 0,
      description: '',
      status: 'Open',
      userId: user?.uid || ''
    });
  };

  const handleEditPurchaseOrder = (poToEdit) => {
    setEditingPurchaseOrder(poToEdit);
    setNewPurchaseOrder({
      poNumber: poToEdit.poNumber || '',
      vendorId: poToEdit.vendorId || '',
      vendorName: poToEdit.vendorName || '',
      date: poToEdit.date || new Date().toISOString().split('T')[0],
      amount: poToEdit.amount || 0,
      description: poToEdit.description || '',
      status: poToEdit.status || 'Open',
      userId: poToEdit.userId || user?.uid || ''
    });
    setIsEditModalOpen(true);
  };

  // Modern, Professional PDF Generation for Purchase Orders
const generateProfessionalPDF = (po, vendor) => {
    // --- MODERN DESIGN CONSTANTS ---
    const PRIMARY_COLOR = '#0077B6'; // Deep Blue
    const LIGHT_BG = '#F0F5F9'; // Very light grey/blue
    const TEXT_COLOR = '#333333';
    const GRAY_TEXT = '#777777';

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = margin; // Starting Y position

    // --- 1. HEADER SECTION: Logo, Title, and Address ---

    // Background rectangle for the top header area
    doc.setFillColor(LIGHT_BG);
    doc.rect(0, 0, pageWidth, 55, 'F'); // Fill entire top section

    try {
      if (shopLogo) {
        doc.addImage(shopLogo, 'PNG', margin, y, 30, 30);
      }
    } catch (error) {
      console.warn('Logo not found, continuing without logo:', error);
    }

    // Shop Name (Primary Accent Color, Larger)
    doc.setFontSize(28).setFont(undefined, 'bold');
    doc.setTextColor(PRIMARY_COLOR);
    doc.text(SHOP_NAME, pageWidth - margin, y + 8, { align: 'right' });

    // Shop Address and Contact (Grey text)
    doc.setFontSize(10).setFont(undefined, 'normal');
    doc.setTextColor(TEXT_COLOR);
    const splitAddress = doc.splitTextToSize(SHOP_ADDRESS, 80);
    doc.text(splitAddress, pageWidth - margin, y + 15, { align: 'right' });
    doc.text(SHOP_PHONE, pageWidth - margin, y + 25, { align: 'right' });

    // Reset Y position and colors for main content
    y = 65;
    doc.setTextColor(TEXT_COLOR);

    // --- 2. PURCHASE ORDER TITLE ---
    doc.setFontSize(24).setFont(undefined, 'bold');
    doc.text('PURCHASE ORDER', margin, y);
    y += 15;

    // --- 3. VENDOR & PO DETAILS SECTION (Organized) ---

    // Column 1: PO Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold').text('PO Details:', margin, y);
    doc.setFont(undefined, 'normal');
    doc.text(`PO #: ${po.poNumber || 'N/A'}`, margin, y + 6);
    doc.text(`Date: ${dateFormatter.format(new Date(po.date || new Date()))}`, margin, y + 12);

    // Column 2: Bill To (Vendor)
    const col2X = pageWidth / 2 + 10;
    doc.setFont(undefined, 'bold').text('Bill To:', col2X, y);
    doc.setFont(undefined, 'normal');

    const vendorLines = [
      vendor.name || 'N/A',
      vendor.contactPhone || '',
      vendor.contactEmail || '',      
      ...(vendor.address ? doc.splitTextToSize(vendor.address, 80) : []) // Narrower width for column
    ].filter(line => line);

    let lineOffset = 0;
    vendorLines.forEach((line, index) => {
        // Handle multiline text from address split
        if (Array.isArray(line)) {
            line.forEach(subLine => {
                doc.text(subLine, col2X, y + 6 + lineOffset);
                lineOffset += 6;
            });
        } else {
            doc.text(line, col2X, y + 6 + lineOffset);
            lineOffset += 6;
        }
    });

    // Ensure 'y' is below the tallest column (PO details or Vendor info)
    y = Math.max(y + 30, y + lineOffset + 10);

    // --- 4. ITEMS TABLE ---
    const tableData = [[
        po.description || 'Goods/Services as per PO',
        '1',
        formatAmount(po.amount, false), // Assuming formatAmount exists
        formatAmount(po.amount, false)
    ]];

    autoTable(doc, {
      startY: y,
      head: [['Description', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'plain', // Use plain theme for modern look
      headStyles: {
          fillColor: PRIMARY_COLOR, // Primary color header
          textColor: 255, // White text
          fontStyle: 'bold',
          fontSize: 10
      },
      styles: {
          textColor: TEXT_COLOR,
          lineColor: '#DDDDDD',
          lineWidth: 0.1, // Subtle lines
          fontSize: 9
      },
      columnStyles: {
          0: { halign: 'left', cellWidth: 90 }, // Description
          1: { halign: 'center', cellWidth: 20 }, // Qty
          2: { halign: 'right' }, // Unit Price
          3: { halign: 'right', fontStyle: 'bold' } // Total
      },
      margin: { left: margin, right: margin }
    });

    const finalY = doc.lastAutoTable.finalY + 10;

    // --- 5. SUMMARY / TOTALS ---

    doc.setFont(undefined, 'normal').setFontSize(10).setTextColor(TEXT_COLOR);
    const summaryX = pageWidth - margin - 50; // Align summary elements
    const valueX = pageWidth - margin;

    // Draw a separating line above total
    doc.setDrawColor(PRIMARY_COLOR);
    doc.line(pageWidth - 75, finalY + 5, pageWidth - margin, finalY + 5);

    // TOTAL (Highlighted)
    doc.setFont(undefined, 'bold').setFontSize(16);
    doc.setTextColor(PRIMARY_COLOR);
    doc.text('TOTAL:', summaryX, finalY + 17, { align: 'right' });
    // Assuming formatAmount1 is the one that adds currency symbol
    doc.text(formatAmount1(po.amount), valueX, finalY + 17, { align: 'right' });


    // --- 6. TERMS & CONDITIONS ---
    const termsY = finalY + 35;
    doc.setFontSize(11).setFont(undefined, 'bold').setTextColor(TEXT_COLOR);
    doc.text('TERMS & CONDITIONS', margin, termsY);
    
    doc.setFontSize(9).setFont(undefined, 'normal').setTextColor(TEXT_COLOR);
    const terms = [
      'Payment Terms: Net 30 days from invoice date.',
      'Delivery: As per agreed schedule.',
      'This Purchase Order is valid for 30 days from issue date.',
      'Please confirm receipt and provide delivery timeline via email.'
    ];
    
    terms.forEach((term, index) => {
      doc.text(`• ${term}`, margin, termsY + 6 + (index * 6));
    });

    // --- 7. FOOTER ---
    doc.setFontSize(8).setTextColor(GRAY_TEXT);
    doc.text('Thank you for your business!', pageWidth / 2, doc.internal.pageSize.height - 15, { align: 'center' });
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });

    return doc;
  };

  // Download PDF Function
  const handleDownloadPDF = (po) => {
    try {
      const vendor = vendors.find(v => v.id === po.vendorId);
      if (!vendor) {
        setError("Vendor information not found");
        setShowModal(true);
        return;
      }
      
      const doc = generateProfessionalPDF(po, vendor);
      doc.save(`PO_${po.poNumber}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      setSuccessMessage(`Purchase Order ${po.poNumber} PDF downloaded successfully`);
      setShowModal(true);
    } catch (error) {
      console.error("PDF generation error:", error);
      setError("Failed to generate PDF: " + error.message);
      setShowModal(true);
    }
  };

  const formatAmount = (value, includeSymbol = true) => {
    const num = Number(value) || 0;
    const formatted = num.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return includeSymbol ? `₦${formatted}` : formatted;
  };

    const formatAmount1 = (value, includeSymbol = true) => {
    const num = Number(value) || 0;
    const formatted = num.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return includeSymbol ? `N${formatted}` : formatted;
  };

  const dateFormatter = new Intl.DateTimeFormat('en-NG', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  // Bulk Import Functions (unchanged)
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileTypeChange = (e) => {
    setFileType(e.target.value);
    setFile(null);
  };

  const handleBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    setImportStatus('Processing file...');
    setImportProgress(0);

    try {
      let poData = [];

      if (fileType === 'excel') {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        poData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        poData = result.data;
      }

      if (poData.length === 0) {
        throw new Error("No purchase order data found in the file");
      }

      setImportStatus(`Importing ${poData.length} purchase orders...`);

      let successCount = 0;
      let errorCount = 0;
      const validStatuses = ['Open', 'Matched', 'Cancelled'];

      for (let i = 0; i < poData.length; i++) {
        try {
          const po = poData[i];

          if (!po.poNumber?.trim() || !po.vendorName?.trim() || !po.amount || parseFloat(po.amount) <= 0) {
            errorCount++;
            continue;
          }

          const vendor = vendors.find(v => v.name === po.vendorName?.trim());
          if (!vendor) {
            console.error(`Vendor not found: ${po.vendorName?.trim()}`);
            errorCount++;
            continue;
          }

          const status = validStatuses.includes(po.status?.trim()) ? po.status.trim() : 'Open';

          const poDataToSave = {
            poNumber: po.poNumber.trim(),
            vendorId: vendor.id,
            vendorName: vendor.name,
            date: po.date || new Date().toISOString().split('T')[0],
            amount: parseFloat(po.amount),
            description: po.description?.trim() || '',
            status,
            userId: user?.uid,
            createdAt: Timestamp.now()
          };

          await addDoc(purchaseOrdersCollectionRef, poDataToSave);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error importing PO ${i + 1}:`, error);
        }

        setImportProgress(Math.round(((i + 1) / poData.length) * 100));
      }

      setImportStatus(`Import complete: ${successCount} successful, ${errorCount} failed`);
      setSuccessMessage(`Bulk import completed: ${successCount} purchase orders added successfully`);
      await fetchPurchaseOrders();
      await fetchBills();
    } catch (error) {
      console.error("Bulk import error:", error);
      setError(`Bulk import failed: ${error.message}`);
      setShowModal(true);
    } finally {
      setUpdate(false);
      setFile(null);
      setTimeout(() => {
        setIsBulkImportModalOpen(false);
        setImportProgress(0);
        setImportStatus('');
      }, 2000);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingPurchaseOrders && !hasFetchedPurchaseOrders) {
      return <p className="loading-text">Loading purchase orders...</p>;
    }
    
    if (filteredPurchaseOrders.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No purchase orders match your search.</p>;
      } else {
        return <p className="loading-text">No purchase orders found. Add your first PO.</p>;
      }
    }
    
    return filteredPurchaseOrders.map((poItem) => {
      const matchedBill = bills.find(bill => 
        bill.purchaseOrderId === poItem.id || 
        (poItem.matchedBillId && bill.id === poItem.matchedBillId)
      );
      
      const unmatchedBillsForVendor = bills.filter(bill => 
        bill.vendorName === poItem.vendorName && 
        !bill.purchaseOrderId
      );
      
      // Determine display status
      const statusDisplay = matchedBill 
        ? `Matched with ${matchedBill.billNumber || poItem.matchedBillNumber}` 
        : poItem.status;
      
      return (
        <div key={poItem.id} className="class-item">
          <div className="column gap-1">
            <p>
              <strong>{poItem.poNumber}</strong><br />
              <span>Vendor: {poItem.vendorName}</span><br />
              <span>Amt: {formatAmount(poItem.amount)}</span>
              <br /><span>Desc: {poItem.description || 'N/A'}</span><br />
              <span className="size5 font-light-blue-cash bold">
                {dateFormatter.format(new Date(poItem.date))} - {statusDisplay}
              </span>
            </p>                        
          </div>
          <div className="row align-center justify-center gap-15">
            <button className="list-btn" onClick={() => handleEditPurchaseOrder(poItem)}>
              <i className="fas fa-edit"></i>
            </button>
            {poItem.status === 'Open' && !matchedBill && (
              <>
                <button 
                  className="list-btn"
                  title="Download PDF"
                  onClick={() => handleDownloadPDF(poItem)}
                >
                  <i className="fas fa-download"></i>
                </button>
                <button className="list-btn" onClick={() => openMatchModal(poItem)}>
                  <i className="fas fa-link"></i>
                </button>
              </>
            )}
            {matchedBill && (
              <button className="list-btn" style={{color: 'green'}}>
                <i className="fas fa-check"></i>
              </button>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Purchase Orders</h2>
            <Link to="#" className="home-link" onClick={async () => {
              await fetchPurchaseOrders();
              await fetchBills();
            }}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>
                Add Order
              </button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>
                Bulk Import
              </button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by PO number or vendor"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Purchase Order"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Purchase Order</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="poNumber" className="input-label">PO Number *</label>
          <input
            type="text"
            name="poNumber"
            className="input-field"
            placeholder="PO Number"
            value={newPurchaseOrder.poNumber}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="vendorId" className="input-label">Vendor *</label>
          <select
            name="vendorId"
            className="input-field"
            value={newPurchaseOrder.vendorId}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Vendor</option>
            {vendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
            ))}
          </select>
          <label htmlFor="date" className="input-label">Date</label>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newPurchaseOrder.date}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="amount" className="input-label">Amount *</label>
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Amount"
            value={newPurchaseOrder.amount}
            onChange={handleInputChange}
            required
            min="0"
            step="0.01"
          />
          <label htmlFor="description" className="input-label">Description</label>
          <textarea
            name="description"
            className="input-field"
            placeholder="Description"
            value={newPurchaseOrder.description}
            onChange={handleInputChange}
          />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>
              Cancel
            </button>
            <button className="login-button" type="button" onClick={handleAddPurchaseOrder}>
              Add Order
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          resetNewPurchaseOrder();
          setEditingPurchaseOrder(null);
        }}
        contentLabel="Edit Purchase Order"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Purchase Order</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="poNumber" className="input-label">PO Number *</label>
          <input
            type="text"
            name="poNumber"
            className="input-field"
            placeholder="PO Number"
            value={newPurchaseOrder.poNumber}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="vendorId" className="input-label">Vendor *</label>
          <select
            name="vendorId"
            className="input-field"
            value={newPurchaseOrder.vendorId}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Vendor</option>
            {vendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
            ))}
          </select>
          <label htmlFor="date" className="input-label">Date</label>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newPurchaseOrder.date}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="amount" className="input-label">Amount *</label>
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Amount"
            value={newPurchaseOrder.amount}
            onChange={handleInputChange}
            required
            min="0"
            step="0.01"
          />
          <label htmlFor="description" className="input-label">Description</label>
          <textarea
            name="description"
            className="input-field"
            placeholder="Description"
            value={newPurchaseOrder.description}
            onChange={handleInputChange}
          />

          <label htmlFor="status" className="input-label">Status</label>
          <select
            name="status"
            className="input-field"
            value={newPurchaseOrder.status}
            onChange={handleInputChange}
          >
            <option value="Open">Open</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              resetNewPurchaseOrder();
              setEditingPurchaseOrder(null);
            }}>
              Cancel
            </button>
            <button className="login-button" type="button" onClick={handleUpdatePurchaseOrder}>
              Update Order
            </button>
          </div>
        </form>
      </Modal>

      {/* Match Modal */}
      <Modal
        isOpen={isMatchModalOpen}
        onRequestClose={() => setIsMatchModalOpen(false)}
        contentLabel="Match Purchase Order"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Match Purchase Order</h2>
        {currentPurchaseOrder && (
          <div className="modal-scroll">
            <p><strong>PO: {currentPurchaseOrder.poNumber}</strong></p>
            <p>Vendor: <strong>{currentPurchaseOrder.vendorName}</strong></p>
            <p>Amount: <strong>{formatAmount(currentPurchaseOrder.amount)}</strong></p>
            <h4>Available Unmatched Bills:</h4>
            {bills.filter(bill => 
              bill.vendorName === currentPurchaseOrder.vendorName && 
              !bill.purchaseOrderId
            ).length > 0 ? (
              bills
                .filter(bill => bill.vendorName === currentPurchaseOrder.vendorName && !bill.purchaseOrderId)
                .map(bill => {
                  const amountDiff = Math.abs((bill.amount || 0) - (currentPurchaseOrder.amount || 0));
                  const isAmountMatch = amountDiff <= 0.01;
                  
                  return (
                    <div key={bill.id} className="class-item column" style={{ 
                      borderLeft: isAmountMatch ? '3px solid green' : '3px solid orange', 
                      marginBottom: '10px',
                      padding: '10px',
                      alignItems: 'flex-start'
                    }}>
                      <p>
                        <strong>{bill.billNumber}</strong> - {formatAmount(bill.amount)}<br />
                        <small>Date: {bill.date || 'N/A'} <br />Due: {bill.dueDate || 'N/A'}</small>
                        {!isAmountMatch && (
                          <span style={{color: 'orange'}}>
                            <br />Amt diff: ₦{amountDiff.toFixed(2)}
                          </span>
                        )}
                      </p>
                      <button 
                        className="list-btn" 
                        onClick={() => handleMatchPurchaseOrder(bill.id)}
                        disabled={!isAmountMatch}
                        style={{ 
                          backgroundColor: isAmountMatch ? '#4CAF50' : '#ff9800',
                          color: 'white'
                        }}
                      >
                        {isAmountMatch ? '✓ Match' : 'Amount Mismatch'}
                      </button>
                    </div>
                  );
                })
            ) : (
              <p>No unmatched bills available for <strong>{currentPurchaseOrder.vendorName}</strong></p>
            )}
            <div className="row align-center gap-1" style={{marginTop: '20px'}}>
              <button 
                className="login-button" 
                type="button" 
                onClick={() => setIsMatchModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
      
      {/* Bulk Import Modal */}
      <Modal
        isOpen={isBulkImportModalOpen}
        onRequestClose={() => {
          setIsBulkImportModalOpen(false);
          setFile(null);
          setImportProgress(0);
          setImportStatus('');
        }}
        contentLabel="Bulk Import Purchase Orders"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Bulk Import Purchase Orders</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '15px' }}>
            <label style={{marginRight: '15px'}}>
              <input
                type="radio"
                name="fileType"
                value="excel"
                checked={fileType === 'excel'}
                onChange={handleFileTypeChange}
              /> Excel File (.xlsx)
            </label>
            <label>
              <input
                type="radio"
                name="fileType"
                value="csv"
                checked={fileType === 'csv'}
                onChange={handleFileTypeChange}
              /> CSV File (.csv)
            </label>
          </div>
          
          <input
            type="file"
            accept={fileType === 'excel' ? '.xlsx, .xls' : '.csv'}
            onChange={handleFileChange}
            className="input-field1"
          />
          
          {importStatus && (
            <div style={{margin: '15px 0'}}>
              <p>{importStatus}</p>
              {importProgress > 0 && (
                <div style={{ width: '100%', backgroundColor: '#ddd', borderRadius: '5px' }}>
                  <div
                    style={{
                      width: `${importProgress}%`,
                      height: '20px',
                      backgroundColor: '#4CAF50',
                      borderRadius: '5px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      color: 'white'
                    }}
                  >
                    {importProgress}%
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="row align-center justify-center gap-15" style={{marginTop: '20px'}}>
            <button
              type="button"
              className="login-button"
              onClick={() => {
                setIsBulkImportModalOpen(false);
                setFile(null);
                setImportProgress(0);
                setImportStatus('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="login-button"
              onClick={handleBulkImport}
              disabled={!file || update}
            >
              {update ? 'Importing...' : 'Import'}
            </button>
          </div>
          
          <div className="import-instructions" style={{ marginTop: '20px', fontSize: '12px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED FIELDS:</strong> poNumber, vendorName, amount</p>
            <p><strong>OPTIONAL FIELDS:</strong> date, description, status</p>
            <p><strong>VALID STATUSES:</strong> Open, Matched, Cancelled</p>
            <p><strong>Vendor Names:</strong> Must match existing vendor names exactly</p>
            <p><strong>FOR EXCEL:</strong> First row should contain column headers</p>
            <p><strong>FOR CSV:</strong> First row should contain column headers, comma-separated</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PurchaseOrders;